import React, { memo } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { monoSx, sectionLabelSx } from '../dashboard/styles';
import { ContainedItem, ENTITIES, Relationship, SelectHandler, Selection } from './entityModel';

const ContainedRow: React.FC<{ item: ContainedItem; focused: boolean }> = ({ item: { Icon, label, desc, card }, focused }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      px: 1.5,
      py: 1.25,
      borderRadius: 2.5,
      border: 1,
      borderColor: focused ? (theme: Theme) => alpha(theme.palette.primary.main, 0.45) : 'divider',
      bgcolor: focused ? 'brand.container' : 'background.paper',
      transition: 'background-color .15s, border-color .15s',
    }}
  >
    <Icon sx={{ fontSize: 20, color: 'text.secondary', mt: '1px' }} />
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}>{desc}</Typography>
    </Box>
    <Box
      component="span"
      sx={{
        flexShrink: 0,
        height: 22,
        px: 1,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'action.hover',
        color: 'text.secondary',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {card}
    </Box>
  </Box>
);

const RelationshipButton: React.FC<{ rel: Relationship; onSelect: SelectHandler }> = ({ rel: { verb, target, card }, onSelect }) => (
  <ButtonBase
    onClick={() => onSelect(target)}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      py: 1,
      pl: 1.5,
      pr: 1.25,
      borderRadius: 2.5,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      textAlign: 'left',
      '&:hover, &.Mui-focusVisible': { bgcolor: 'action.hover' },
    }}
  >
    <Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.4, color: 'text.secondary' }}>
      {verb}{' '}
      <Box component="strong" sx={{ color: 'text.primary' }}>
        {ENTITIES[target].label.toLowerCase()}
      </Box>
    </Typography>
    <Box component="span" sx={{ ...monoSx, fontSize: 11, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
      {card}
    </Box>
    <ArrowForward sx={{ fontSize: 18, color: 'text.disabled' }} />
  </ButtonBase>
);

interface EntityDetailsPanelProps {
  selection: Selection;
  onSelect: SelectHandler;
}

/** Sticky side panel describing the selected entity: what it contains and what it links to. */
const EntityDetailsPanel: React.FC<EntityDetailsPanelProps> = ({ selection, onSelect }) => {
  const { Icon, label, level, desc, color, items, rels } = ENTITIES[selection.id];
  const tint = (theme: Theme) => alpha(theme.palette[color].main, 0.12);

  return (
    <Paper
      variant="outlined"
      component="aside"
      aria-label={`${label} details`}
      sx={{
        flex: '1 1 360px',
        maxWidth: { md: 460 },
        minWidth: { xs: 0, sm: 300 },
        position: { md: 'sticky' },
        top: { md: 88 },
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2.5, pb: 2, display: 'flex', gap: 1.75, alignItems: 'flex-start', borderBottom: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 3,
            bgcolor: tint,
            color: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 600 }}>
              {label}
            </Typography>
            <Box
              component="span"
              sx={{ height: 22, px: 1, borderRadius: 2.75, bgcolor: tint, color: `${color}.main`, fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center' }}
            >
              {level}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>{desc}</Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography component="h3" sx={sectionLabelSx}>
          Contains
        </Typography>
        {items.map((item) => (
          <ContainedRow key={item.label} item={item} focused={item.key !== undefined && item.key === selection.focus} />
        ))}
      </Box>

      <Box sx={{ px: 2.5, pt: 0.5, pb: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography component="h3" sx={sectionLabelSx}>
          Relationships
        </Typography>
        {rels.map((rel) => (
          <RelationshipButton key={`${rel.verb}-${rel.target}`} rel={rel} onSelect={onSelect} />
        ))}
      </Box>
    </Paper>
  );
};

export default memo(EntityDetailsPanel);
